import type { GraphNode as NodeType } from "~/js/graph/node-socket-connection";
import type { nodeNewExecutor } from "~/js/utilities/changeNodeExecutorOnCustom";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { Default_Executor_Text, onError } from "~/js/utilities/executor";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { ChatGptApi } from "~/solidJs/shared/api/ChatGptApi";
import {
	getPrivateMessageFromError,
	getPublicMessageFromError,
	isCancelError
} from "~/solidJs/shared/helpers";
import {
	collectNodeDataForTask,
	getTipComputingText,
	prepeareFileParamsForTask
} from "~/solidJs/shared/helpers/nodeHelpers";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types";
import { ExecutionStatus, NodeExecutor } from "../../node-executor";

const globalIdToGpt = {
	"Phygital Creator/phygc-rnd-chat-gpt-vision": ChatGptApi.getChatGptImageVision.bind(ChatGptApi),
	"Phygital Creator/phygc-rnd-chat-gpt-4o": ChatGptApi.getChatGpt4o.bind(ChatGptApi),
	"Phygital Creator/phygc-rnd-chat-gpt-prompt": ChatGptApi.getChatGpt4oPrompt.bind(ChatGptApi),
	"Phygital Creator/phygc-rnd-chat-gpt-prompt-extend":
		ChatGptApi.getPromptExtend.bind(ChatGptApi),
	"Phygital Creator/phygc-rnd-chat-gpt-4o-describe":
		ChatGptApi.getChatGptDescribe.bind(ChatGptApi)
} as const;

/**
 * Сериализует NodeDefinition в структурированный объект для передачи в промпт
 */
const serializeNodeDefinition = (node: NodeType) => {
	if (!node.definition) return null;

	const definition = node.definition;

	const serializeSocket = (socket: any) => ({
		name: socket.name,
		displayName: socket.displayName || socket.name,
		dataType: socket.dataType,
		defaultValue: socket.defaultValue,
		description: socket.optionalInfo?.description || undefined,
		group: socket.optionalInfo?.group || undefined,
		limits: socket.optionalInfo?.valueOptions?.limits || undefined,
		values: socket.optionalInfo?.valueOptions?.values || undefined
	});

	return {
		globalId: definition.globalId,
		name: definition.name,
		description: definition.description,
		category: definition.category,
		inputs: definition.inputs?.map(serializeSocket) || [],
		outputs: definition.outputs?.map(serializeSocket) || [],
		params: definition.params?.map(serializeSocket) || []
	};
};

const insertPremTaskIntoPrompt = (prompt: string, node: NodeType) => {
	try {
		const inputSocket = node.getSocketsIn();
		const prevSocket =
			inputSocket.find((socket) => socket.name === "init_img" && socket.connections.length) ||
			inputSocket.find(
				(socket) => socket.name === "text_prompt" && socket.connections.length
			);
		let enrichedPrompt = prompt;
		if (!prevSocket) return enrichedPrompt;
		const connectedNode = prevSocket.connections?.[0]?.socketOut?.node;
		if (!connectedNode) return enrichedPrompt;

		const nodeSchema = serializeNodeDefinition(connectedNode);

		if (nodeSchema && enrichedPrompt.includes("{{node_json}}")) {
			const nodeSchemaJson = JSON.stringify(nodeSchema);
			enrichedPrompt = enrichedPrompt.replace("{{node_json}}", nodeSchemaJson);
		}

		if (connectedNode.meta.taskSchema && enrichedPrompt.includes("{{task_json}}")) {
			const taskSchemaJson = JSON.stringify(connectedNode.meta.taskSchema);
			enrichedPrompt = enrichedPrompt.replace("{{task_json}}", taskSchemaJson);
		}
		return enrichedPrompt;
	} catch {}
	return prompt;
};

export const chatGptVisionCustomExecuter: nodeNewExecutor = async (node) => {
	const executor = new NodeExecutor(node);
	executor.useEnterExitTriggers();
	executor.setStatusEnabled(true);
	executor.updateStatus(ExecutionStatus.Ready, Default_Executor_Text);
	executor.setExecuteFunction(async (cancelController: AbortController) => {
		let interval: NodeJS.Timeout;
		try {
			node?.eventor?.invoke("preExecutePreparing");
			const executionStartTs = Date.now();
			EventAnalytics.executeNode(node.globalId, executionStartTs);

			executor.DOMStyle.executeBlock.start();

			// Starting phase - collecting data
			executor.updateStatus(ExecutionStatus.Starting, "Collecting node data...");
			const nodeData = await collectNodeDataForTask(node, node.executuionBlock);

			executor.updateStatus(ExecutionStatus.Starting, "Sync with storage...");
			await prepeareFileParamsForTask(nodeData, node.executuionBlock, node);

			let text_prompt =
				(nodeData.inputs.find((socket) => socket.name === "text_prompt")
					?.value as string) || "";

			if (getUserStore()?.group === UserSubscriptionPlanEnum.Superuser) {
				text_prompt = insertPremTaskIntoPrompt(text_prompt, node);
			}
			const imageId = nodeData.inputs.find((socket) => socket.name === "init_img")?.value;

			// Computing phase - show tip and animate progress
			executor.updateStatus(ExecutionStatus.Computing, getTipComputingText());

			const { message } = await (
				globalIdToGpt[node.globalId as keyof typeof globalIdToGpt] ||
				ChatGptApi.getChatGpt4o
			)(text_prompt as string, (imageId as number) || 0, cancelController.signal);

			node.updateOutputs([
				{
					name: "description",
					type: "text",
					value: message
				}
			]);

			const executeNodeSuccessTs = Date.now();
			EventAnalytics.executeNodeSuccess(
				node.globalId,
				executeNodeSuccessTs,
				"uuid",
				executeNodeSuccessTs - executionStartTs
			);

			executor.updateStatus(ExecutionStatus.Done, "Successful");
			executor.DOMStyle.executeBlock.finish();
		} catch (e: unknown) {
			if (isCancelError(e)) return;
			EventAnalytics.sendErrorInfo("execute-node-error", {
				message: getPrivateMessageFromError(e),
				errorMessage: getPrivateMessageFromError(e),
				globalId: node.globalId
			});
			onError(
				executor,
				getPublicMessageFromError(e) || "Unknown execution error",
				node,
				interval!,
				-1
			);
		}
	});
};
